package com.routexk.quiz.funnel

import com.routexk.quiz.i18n.t
import com.routexk.quiz.i18n.tv

/** [gaId] is the question_id sent to GA4 (see GA4 event spec, "Quiz Answer Values"). */
enum class QuizStepId(val storageKey: String, val gaId: String) {
    CITY("city", "work_city"),
    STOPS("stops_per_day", "stops_per_day"),
    MILES("miles_per_shift", "miles_per_shift"),
    HOURS("hours_per_shift", "hours_per_shift"),
    DAYS("days_per_week", "days_per_week"),
    FUEL("fuel_per_week", "weekly_fuel_spend"),
    EARNINGS("hourly_earnings", "hourly_earnings"),
}

/**
 * A quiz step. [options] == null means free-text input (city step).
 * [midpoints] carry the numeric value each option feeds into [EstimateCalculator],
 * index-aligned with [options].
 */
data class QuizStep(
    val id: QuizStepId,
    val question: String,
    val options: List<String>? = null,
    val midpoints: List<Double>? = null,
    val inputPlaceholder: String? = null,
    val helper: String? = null,
    /** answer_value sent to GA4 per option, index-aligned with [options] (see GA4 event spec). */
    val gaAnswerValues: List<String>? = null,
)

object QuizDefinition {

    // Rebuilt per read so the active language (see i18n.I18n) is applied. The
    // numeric [midpoints] are language-independent and drive the calculator.
    val steps: List<QuizStep> get() = listOf(
        QuizStep(
            id = QuizStepId.CITY,
            question = t("What city do you work in?", "¿En qué ciudad trabajas?"),
            inputPlaceholder = t("New York, NY, USA", "Miami, FL, EE. UU."),
            helper = FunnelCopy.CITY_HELPER,
        ),
        QuizStep(
            id = QuizStepId.STOPS,
            question = t("How many stops a day do you drive?", "¿Cuántas paradas haces al día?"),
            options = tv(
                listOf(
                    "Up to 10 stops / day",
                    "11–20 stops / day",
                    "21–50 stops / day",
                    "51–100 stops / day",
                    "101–200 stops / day",
                    "200+ stops / day",
                ),
                listOf(
                    "Hasta 10 paradas / día",
                    "11–20 paradas / día",
                    "21–50 paradas / día",
                    "51–100 paradas / día",
                    "101–200 paradas / día",
                    "200+ paradas / día",
                ),
            ),
            // Point estimates per results spec Step 1 (wide/open buckets skew low)
            midpoints = listOf(7.0, 15.0, 32.0, 68.0, 140.0, 240.0),
            gaAnswerValues = listOf("up_to_10", "11_20", "21_50", "51_100", "101_200", "200_plus"),
        ),
        QuizStep(
            id = QuizStepId.MILES,
            question = t("How many miles per shift do you drive?", "¿Cuántas millas manejas por turno?"),
            options = tv(
                listOf(
                    "Less than 20 miles",
                    "20–50 miles",
                    "50–100 miles",
                    "More than 100 miles",
                ),
                listOf(
                    "Menos de 20 millas",
                    "20–50 millas",
                    "50–100 millas",
                    "Más de 100 millas",
                ),
            ),
            midpoints = listOf(14.0, 33.0, 72.0, 130.0),
            gaAnswerValues = listOf("under_20", "20_50", "50_100", "over_100"),
        ),
        QuizStep(
            id = QuizStepId.HOURS,
            question = t("How many hours do you work per shift?", "¿Cuántas horas trabajas por turno?"),
            options = tv(
                listOf("Less than 4", "4–5", "6–7", "8–9", "10+"),
                listOf("Menos de 4", "4–5", "6–7", "8–9", "10+"),
            ),
            midpoints = listOf(3.0, 4.5, 6.5, 8.5, 11.0),
            gaAnswerValues = listOf("under_4", "4_5", "6_7", "8_9", "10_plus"),
        ),
        QuizStep(
            id = QuizStepId.DAYS,
            question = t("How many days per week do you work?", "¿Cuántos días a la semana trabajas?"),
            options = tv(
                listOf(
                    "Up to 3 days / week",
                    "4–5 days / week",
                    "5–6 days / week",
                ),
                listOf(
                    "Hasta 3 días / semana",
                    "4–5 días / semana",
                    "5–6 días / semana",
                ),
            ),
            midpoints = listOf(2.5, 4.5, 5.5),
            gaAnswerValues = listOf("up_to_3", "4_5", "5_6"),
        ),
        QuizStep(
            id = QuizStepId.FUEL,
            question = t("How much do you spend per week on fuel?", "¿Cuánto gastas por semana en combustible?"),
            // Buckets closed per spec Step 0.3 ($91–100 gap fixed); "I don't know" -> null point estimate
            options = tv(
                listOf(
                    "Under \$50",
                    "\$51–70",
                    "\$71–80",
                    "\$81–90",
                    "\$91–100",
                    "\$100+",
                    "I don't know",
                ),
                listOf(
                    "Menos de \$50",
                    "\$51–70",
                    "\$71–80",
                    "\$81–90",
                    "\$91–100",
                    "\$100+",
                    "No lo sé",
                ),
            ),
            midpoints = listOf(42.0, 60.0, 75.0, 85.0, 95.0, 115.0, -1.0),
            gaAnswerValues = listOf("under_50", "51_70", "71_80", "81_90", "91_100", "100_plus", "dont_know"),
        ),
        QuizStep(
            id = QuizStepId.EARNINGS,
            question = t("How much do you make per hour on average?", "¿Cuánto ganas por hora en promedio?"),
            options = tv(
                listOf(
                    "\$10–15 / hour",
                    "\$16–20 / hour",
                    "\$21–25 / hour",
                    "\$26–30 / hour",
                    "\$31–35 / hour",
                    "\$36–45 / hour",
                    "\$45+ / hour",
                ),
                listOf(
                    "\$10–15 / hora",
                    "\$16–20 / hora",
                    "\$21–25 / hora",
                    "\$26–30 / hora",
                    "\$31–35 / hora",
                    "\$36–45 / hora",
                    "\$45+ / hora",
                ),
            ),
            midpoints = listOf(12.5, 18.0, 23.0, 28.0, 33.0, 40.0, 50.0),
            gaAnswerValues = listOf("10_15", "16_20", "21_25", "26_30", "31_35", "36_45", "45_plus"),
        ),
    )

    fun step(id: QuizStepId): QuizStep = steps.first { it.id == id }
}
